import React, { useState, useEffect, useRef } from "react";
import { List, Spin, Input, Button, Form, Row, Col, Avatar, Typography, message, Modal } from "antd";
import Title from "antd/es/typography/Title";
import { auth, firestore } from "../../firebase";
import { doc, onSnapshot } from "firebase/firestore";
import { sendAnswer, cerrarSolicitud } from "../../servicios/solicitudes/servicios.ts";
import useNotification from "antd/es/notification/useNotification";

const { TextArea } = Input;

const ChatComponent = ({ solicitudInfo }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const messagesEndRef = useRef(null);
  const openNotificationWithIcon = useNotification();
  const [cerrando, setCerrando] = useState(false);
  // Cerrar solicitud
  const handleCerrarSolicitud = async () => {
    Modal.confirm({
      title: '¿Cerrar esta solicitud?',
      content: 'Esta acción no se puede deshacer. ¿Deseas continuar?',
      okText: 'Cerrar',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: async () => {
        setCerrando(true);
        try {
          const validedToken = await auth.currentUser.getIdToken(true);
          const response = await cerrarSolicitud(validedToken, { folio: solicitudInfo.folio, sucursal: solicitudInfo.sucursal || 'Centro' });
          if (response.success) {
            message.success('Solicitud cerrada correctamente.');
            // Opcional: refrescar estado local o recargar solicitudes
          } else {
            message.error(response?.data?.msg || 'No se pudo cerrar la solicitud.');
          }
        } catch (e) {
          message.error('Error al cerrar la solicitud.');
        }
        setCerrando(false);
      }
    });
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const saveAnswer = async (info) => {
    setProcessing(true);
    try {
      const body = {
        mensaje: info.respuesta,
        sucursal: solicitudInfo.sucursal || "Centro",
        folio: solicitudInfo.folio,
      };

      const validedToken = await auth.currentUser.getIdToken(true);
      const response = await sendAnswer(validedToken, body);
      if (response && response.success) {
        openNotificationWithIcon(
          "success",
          "Respuesta enviada"
        );
      } else {
        openNotificationWithIcon(
          "warning",
          "Error al momento de mandar respuesta, intente más tarde",
          response.error.msg ? response.error.msg : response.error
        );
      }
    } catch (e) {
      openNotificationWithIcon(
        "error",
        "Error",
        `Se presento un error al enviar respuesta, intente más tarde.`
      );
    }
    setProcessing(false);
  };

  useEffect(() => {
    if (solicitudInfo) {
      const docRef = doc(firestore, "solicitud", solicitudInfo.folio);
      // Set up subscription
      const unsubscribe = onSnapshot(docRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
          const solicitudData = docSnapshot.data();
          setMessages(solicitudData.mensajes || []);
          setLoading(false);
        } else {
          // ...
        }
      });

      return () => {
        unsubscribe();
      };
    }
  }, [solicitudInfo]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: '#f7f8fa' }}>
      <div style={{ padding: '16px 24px', borderBottom: '1px solid #eee', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <Typography.Title level={5} style={{ margin: 0 }}>Folio: {solicitudInfo.folio} | {solicitudInfo.nombre}</Typography.Title>
          <Typography.Text type="secondary">Tel: {solicitudInfo.telefono} | Origen: {solicitudInfo.origen}</Typography.Text>
        </div>
        {solicitudInfo.status === 'Respondida' && (
          <Button danger loading={cerrando} onClick={handleCerrarSolicitud} style={{ marginLeft: 16 }}>
            Cerrar Solicitud
          </Button>
        )}
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, background: '#f7f8fa' }} className="ccg-chat-solicitudes">
        {loading && <Spin tip="Cargando mensajes..." />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {messages.map((item, idx) => (
            <div key={idx}>
              {/* Mensaje del usuario */}
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8 }}>
                <Avatar style={{ background: '#1890ff' }}>{solicitudInfo.nombre?.[0] || 'U'}</Avatar>
                <div style={{ background: '#fff', borderRadius: 12, padding: 12, maxWidth: 400, boxShadow: '0 1px 4px #e6e6e6' }}>
                  <div style={{ fontSize: 13, color: '#888' }}>{item.creacion}</div>
                  <div style={{ fontSize: 16 }}>{item.mensaje}</div>
                </div>
              </div>
              {/* Respuesta del sistema */}
              {item.respuesta && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 4, gap: 8 }}>
                  <div style={{ background: '#e6f7ff', borderRadius: 12, padding: 12, maxWidth: 400, boxShadow: '0 1px 4px #e6e6e6', textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: '#888' }}>{item.respuesta_creacion}</div>
                    <div style={{ fontSize: 16 }}>{item.respuesta}</div>
                  </div>
                  <Avatar style={{ background: '#52c41a' }}>CCG</Avatar>
                </div>
              )}
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </div>
      {solicitudInfo.status !== 'Cerrado' ? (
        <div style={{ borderTop: '1px solid #eee', background: '#fff', padding: 16 }}>
          <Form onFinish={saveAnswer} layout="inline" style={{ margin: 0 }}>
            <Form.Item
              name="respuesta"
              style={{ flex: 1, marginRight: 8, marginBottom: 0 }}
              rules={[
                {
                  required: true,
                  message: "Por favor ingrese una respuesta",
                },
              ]}
            >
              <Input.TextArea rows={1} placeholder="Escribe una respuesta..." autoSize={{ minRows: 1, maxRows: 4 }} style={{ resize: 'none' }} />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                loading={processing}
                style={{ minWidth: 90 }}
              >
                Enviar
              </Button>
            </Form.Item>
          </Form>
        </div>
      ) : (
        <div style={{ borderTop: '1px solid #eee', background: '#fff', padding: 16, textAlign: 'center', color: '#888' }}>
          <Typography.Text type="secondary">Esta solicitud está cerrada. No es posible enviar más mensajes.</Typography.Text>
        </div>
      )}
    </div>
  );
};

export default ChatComponent;
